import { useContext, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';

import { AnnouncementBanners } from '@components/AnnouncementBanner/AnnouncementBanner';
import Footer from '@components/Footer/Footer';
import Header from '@components/Header/HeaderNav';
import { IS_NATERA } from '@constants/constants';
import { ORDERS_AND_REPORTS } from '@constants/routes';
import useAuth from '@hooks/useAuth';
import { getUserProfile } from '@store/actions';
import { Context } from '@store/store';
import { identifyHubSpotUser } from '@utils/hubspot';
import { identifyUser } from '@utils/openreplay';

import * as Sentry from '@sentry/react';
import routes from './routes';
import ProtectedRoute from './ProtectedRoute';
import { Actions } from '../types/app';

const RouterProvider = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [state, dispatch] = useContext(Context);
  const { isLoggedIn, onInvalidAuth } = useAuth();
  const userProfile = state.userProfile;
  const showAnnouncements = isLoggedIn && !IS_NATERA;

  // Redirect to /requestions page if logged in and on root path
  useEffect(() => {
    if (isLoggedIn && pathname === '/') {
      navigate(ORDERS_AND_REPORTS);
    }
  }, [isLoggedIn, navigate, pathname]);

  // set Sentry user context and identify user in OpenReplay
  useEffect(() => {
    if (isLoggedIn && userProfile) {
      Sentry.setUser({
        id: userProfile.provider_id,
        email: userProfile.email,
      });

      identifyHubSpotUser(userProfile);

      // Identify user in OpenReplay
      identifyUser(userProfile.provider_id, userProfile.fullName, userProfile.email);
    } else if (isLoggedIn) {
      Sentry.setUser(null);
    }
  }, [isLoggedIn, userProfile]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const handleUserProfile = async () => {
      try {
        const userProfile = await getUserProfile();

        dispatch({ type: Actions.USER_PROFILE_UPDATED, payload: userProfile });
      } catch {
        onInvalidAuth();
      }
    };

    handleUserProfile();
  }, [dispatch, isLoggedIn, onInvalidAuth]);

  return (
    <Routes>
      {routes.map(route => (
        <Route
          path={route.path}
          element={
            <ProtectedRoute isPrivate={route.private} onlyAuth={route.onlyAuth}>
              {route.oldLayout ? (
                route.element
              ) : (
                <>
                  <Header />
                  {route.PageContainer && (
                    <route.PageContainer>
                      {/* page-container is a flex row, so the banner needs its own column above it. */}
                      <div className='page-container__stack'>
                        {showAnnouncements && (
                          <Container fluid='xxl'>
                            <AnnouncementBanners />
                          </Container>
                        )}
                        <Container fluid='xxl' className='d-flex flex-grow-1 position-relative'>
                          {route.element}
                        </Container>
                      </div>
                    </route.PageContainer>
                  )}
                  {!route.PageContainer && (
                    <Container fluid='xxl' className='flex-grow-1 position-relative'>
                      {showAnnouncements && <AnnouncementBanners />}
                      {route.element}
                    </Container>
                  )}
                  <Footer />
                </>
              )}
            </ProtectedRoute>
          }
          key={route.path}
        />
      ))}

      <Route path='*' element={<Navigate to='/' state={{ pathname }} replace />} />
    </Routes>
  );
};

export default RouterProvider;
